import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectDisplayedGradient, selectGradientData } from '../../store/system/system.selector.js';
import { selectAuthIsReady, selectTeamIndex, selectUser } from '../../store/user/user.selector.js';
import { setDisplayedGradient } from '../../store/system/system.action.js';
import GradientBG from '../../components/gradient-bg/GradientBG.js';
import PageNav from '../../components/system/page-nav/PageNav.js';
import { createFirestoreAdapter } from '../../utils/firebase/mlbTheShow.js';
import { createLocalStorageAdapter } from '../../utils/mlb-the-show/storage.js';
import useTrackerData from './useTrackerData.js';
import ShowTracker from './ShowTracker.js';
import {
  Banner,
  Empty,
  GradientWrap,
  HeaderWrap,
  Lead,
  Main,
  Page,
  TextButton,
} from './MlbTheShowPage.styles.js';

const guestStorage = createLocalStorageAdapter();

export default function MlbTheShowPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const displayedGradient = useSelector(selectDisplayedGradient);
  const teamIndex = useSelector(selectTeamIndex);
  const gradientData = useSelector(selectGradientData);
  const authIsReady = useSelector(selectAuthIsReady);
  const user = useSelector(selectUser);

  const signedIn = Boolean(authIsReady && user?.uid);
  const storage = useMemo(() => {
    if (!authIsReady) return null;
    return user?.uid ? createFirestoreAdapter(user.uid) : guestStorage;
  }, [authIsReady, user?.uid]);
  const data = useTrackerData(storage);

  useEffect(() => {
    if (!displayedGradient) dispatch(setDisplayedGradient('A'));
  }, [dispatch, displayedGradient]);

  return (
    <Page>
      <GradientWrap>
        <GradientBG setGradient={gradientData[teamIndex] ?? gradientData[0]} />
      </GradientWrap>
      <HeaderWrap>
        <PageNav />
      </HeaderWrap>
      <Main>
        <h1 className="pageHeader">MLB® The Show™ Tracker</h1>
        <Lead>
          Head-to-head records.
          {' '}
          {signedIn
            ? 'Saved to your account.'
            : (
              <>
                Saved in this browser until you
                {' '}
                <TextButton type="button" onClick={() => navigate('/auth')}>sign in</TextButton>
                .
              </>
            )}
        </Lead>

        {data.error && (
          <Banner>
            {data.error}
            {!data.loaded && (
              <>
                {' '}
                <TextButton type="button" onClick={data.retry}>Try again</TextButton>
              </>
            )}
          </Banner>
        )}

        {!data.ready || !data.loaded ? (
          <Empty>{data.ready && data.error ? 'The tracker could not be loaded.' : 'Loading…'}</Empty>
        ) : (
          <ShowTracker
            key={storage === guestStorage ? 'guest' : user?.uid}
            players={data.players}
            games={data.games}
            onAddPlayer={data.addPlayer}
            onRemovePlayer={data.removePlayer}
            onAddGame={data.addGame}
            onRemoveGame={data.removeGame}
          />
        )}
      </Main>
    </Page>
  );
}
